import React, { useState } from 'react';
import { Modal, Button, ToggleSwitch } from 'components/common';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaPhone, FaEnvelope, FaCommentDots, FaEllipsisH } from 'react-icons/fa';
import { MdEvent } from 'react-icons/md';
import { createActivity as _createActivity, updateActivity as _updateActivity } from 'redux/crm/project/projectActivitySlice';
import TinymceEditor from 'components/common/TinymceEditor';
import ContactSelect  from './ContactSelect';
import AssigneeSelect from './AssigneeSelect';
import FileUploader from './FileUploader';
import { INPUT_CLS } from 'helpers/formStyles';

const TYPES = [
  { value: 'meeting', label: 'Meeting | Present | Demo', icon: MdEvent },
  { value: 'call',    label: 'Call',                     icon: FaPhone },
  { value: 'email',   label: 'Email',                    icon: FaEnvelope },
  { value: 'sms',     label: 'SMS | Chat',               icon: FaCommentDots },
  { value: 'other',   label: 'Other',                    icon: FaEllipsisH },
];

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = () => ({
  activity_type: 'meeting', name: '', other_contact: '',
  date: today(), start_time: '', end_time: '', note: '', description: '', status: 'todo',
});

const ActivityForm = ({
  open, onClose, projectId, record,
  createAction = _createActivity,
  updateAction = _updateActivity,
  submittingSelector = s => s.crm_project_activity.submitting,
}) => {
  const dispatch   = useDispatch();
  const submitting = useSelector(submittingSelector);

  const toDate = (dt) => dt ? new Date(dt).toISOString().slice(0, 10) : '';
  const toTime = (dt) => dt ? new Date(dt).toISOString().slice(11, 16) : '';

  const [form, setForm] = useState(() => record ? {
    activity_type: record.activity_type || 'meeting',
    name:          record.name          || '',
    other_contact: record.other_contact || '',
    date:          toDate(record.start_time),
    start_time:    toTime(record.start_time),
    end_time:      toTime(record.end_time),
    note:          record.note          || '',
    description:   record.description   || '',
    status:        record.status        || 'todo',
  } : EMPTY());

  const [contact,  setContact]  = useState(
    record?.contact_id ? { id: record.contact_id, name: record.contact_name || '' } : null
  );
  const [assignee, setAssignee] = useState(
    record?.assignee_id ? { id: record.assignee_id, name: record.assignee_name || '' } : null
  );
  const [files, setFiles] = useState([]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Interaction name is required.'); return; }

    const { date, start_time, end_time, ...rest } = form;
    const data = {
      ...rest,
      start_time:  date && start_time ? `${date}T${start_time}` : '',
      end_time:    date && end_time   ? `${date}T${end_time}`   : '',
      contact_id:  contact?.id  || null,
      assignee_id: assignee?.id || null,
    };

    const action = record
      ? updateAction({ projectId, id: record.id, data, files })
      : createAction({ projectId, data, files });

    const res = await dispatch(action);
    if (res.meta.requestStatus === 'fulfilled') {
      toast.success(record ? 'Activity updated.' : 'Activity created.');
      onClose();
    } else {
      toast.error(res.payload || 'Failed to save activity.');
    }
  };

  const inputClass = INPUT_CLS;
  const labelClass = "tw-text-xs tw-font-semibold tw-text-slate-600 tw-mb-1 tw-block";

  return (
    <Modal show={open} onHide={onClose} title={record ? 'Edit Activity' : 'Add Activity'} size="xl" backdropClick={false}>
      <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
        {/* Activity type */}
        <div>
          <label className={labelClass}>Activity type <span className="tw-text-red-500">*</span></label>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {TYPES.map(({ value, label, icon: Icon }) => {
              const active = form.activity_type === value;
              return (
                <button key={value} type="button" onClick={() => set('activity_type', value)}
                  className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-xl tw-text-xs tw-font-medium tw-border tw-cursor-pointer tw-transition-colors
                    ${active
                      ? 'tw-bg-blue-600 tw-text-white tw-border-blue-600'
                      : 'tw-bg-white tw-text-slate-600 tw-border-slate-300 hover:tw-border-blue-400 hover:tw-text-blue-600'
                    }`}>
                  <Icon size={12} /> {label}
                  {active && <span className="tw-ml-0.5 tw-text-blue-200">✓</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Name */}
        <div>
          <label className={labelClass}>Interaction name <span className="tw-text-red-500">*</span></label>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            placeholder="Interaction name" className={inputClass} />
        </div>

        {/* Contact + Assignee */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <ContactSelect
            label="Contact"
            value={contact}
            onChange={setContact}
            projectId={projectId}
          />
          <AssigneeSelect
            label="Assignee"
            value={assignee}
            onChange={setAssignee}
          />
        </div>

        {/* Other contact */}
        {/* <div>
          <label className={labelClass}>Other contact</label>
          <input value={form.other_contact} onChange={e => set('other_contact', e.target.value)}
            placeholder="Other contact name" className={inputClass} />
        </div> */}

        {/* Date */}
        <div>
          <label className={labelClass}>Date</label>
          <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className={inputClass} />
        </div>

        {/* Start Time / End Time */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <div>
            <label className={labelClass}>Start Time</label>
            <input type="time" value={form.start_time} onChange={e => set('start_time', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>End Time</label>
            <input type="time" value={form.end_time} onChange={e => set('end_time', e.target.value)} className={inputClass} />
          </div>
        </div>

        {/* Mark as completed */}
        <ToggleSwitch
          value={form.status === 'done'}
          onChange={(checked) => set('status', checked ? 'done' : 'todo')}
          label="Mark as completed"
        />

        {/* Note (short) */}
        {/* Note (rich editor) */}
        <div>
          <label className={labelClass}>Note</label>
          <TinymceEditor
            value={form.note}
            handleChange={(val) => set('note', val)}
            height="220px"
            initOverride={{
              menubar:           false,
              toolbar:           'bold italic underline | bullist numlist | link | image',
              plugins:           'lists link image',
              paste_data_images: true,
            }}
          />
        </div>

        {/* File upload */}
        <div>
          <label className={labelClass}>Attachments</label>
          <FileUploader
            files={files}
            onChange={setFiles}
            existingAttachments={record?.attachments || []}
          />
        </div>

        {/* Actions */}
        <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-1">
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

ActivityForm.propTypes = {
  open:               PropTypes.bool.isRequired,
  onClose:            PropTypes.func.isRequired,
  projectId:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  record:             PropTypes.object,
  createAction:       PropTypes.func,
  updateAction:       PropTypes.func,
  submittingSelector: PropTypes.func,
};

export default ActivityForm;
